<script setup>
// Senha definida pelo administrador, para quem perdeu o acesso e não consegue usar o e-mail de
// redefinição.
//
// ⚠️ A SENHA NÃO É ENVIADA PELO SISTEMA. Quem define é o administrador, aqui, e combina com a
// pessoa pelo canal que os dois já usam. Depois de salva ela não é mostrada de novo em lugar
// nenhum: o que fica no banco é o mesmo resumo criptográfico de qualquer outra senha.
//
// A pessoa é obrigada a trocar no próximo acesso, e é isso que impede a senha provisória de
// virar a senha definitiva dela.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgentSenhaAPI from 'dashboard/api/fandata/agentSenha';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const senha = ref('');
const confirmacao = ref('');
const salvando = ref(false);
const mostrar = ref(false);

// A regra, item a item, conferida enquanto digita. Dizer "senha inválida" depois de enviar é a
// pior forma de ensinar uma regra: a pessoa acerta por tentativa e erro.
const requisitos = computed(() => [
  { chave: 'TAMANHO', ok: senha.value.length >= 6 },
  { chave: 'MAIUSCULA', ok: /[A-Z]/.test(senha.value) },
  { chave: 'MINUSCULA', ok: /[a-z]/.test(senha.value) },
  { chave: 'NUMERO', ok: /[0-9]/.test(senha.value) },
  { chave: 'ESPECIAL', ok: /[^A-Za-z0-9]/.test(senha.value) },
]);

const todosOsRequisitos = computed(() => requisitos.value.every(r => r.ok));
const combinam = computed(
  () => senha.value.length > 0 && senha.value === confirmacao.value
);

const salvar = async () => {
  if (!combinam.value || !todosOsRequisitos.value) return;
  salvando.value = true;
  try {
    await AgentSenhaAPI.definir(props.id, senha.value);
    useAlert(t('AGENT_MGMT.SENHA.SUCESSO', { nome: props.name }));
    emit('close');
  } catch (error) {
    // A mensagem vem do próprio sistema (regra de força da senha): reescrevê-la aqui esconderia
    // o motivo real da recusa.
    //
    // ⚠️ A CHAVE É `error`, e não `message`. Ler a errada foi o que fez a tela dizer "não foi
    // possível" para toda recusa, inclusive para senha fraca, escondendo justamente a instrução
    // de que a pessoa precisa.
    const resposta = error?.response?.data;
    useAlert(resposta?.error || resposta?.message || t('AGENT_MGMT.SENHA.ERRO'));
  } finally {
    salvando.value = false;
    senha.value = '';
    confirmacao.value = '';
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.SENHA.TITULO', { nome: name })"
      :header-content="$t('AGENT_MGMT.SENHA.SUBTITULO')"
    />
    <form class="flex flex-col w-full gap-4 p-8 pt-4" @submit.prevent="salvar">
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_MGMT.SENHA.CAMPO') }}
        </span>
        <input
          v-model="senha"
          :type="mostrar ? 'text' : 'password'"
          autocomplete="new-password"
          class="mb-0"
          :placeholder="$t('AGENT_MGMT.SENHA.CAMPO_DICA')"
        />
      </label>

      <!-- A regra conferida enquanto digita: a recusa do sistema vem em inglês e em lista longa,
           e é melhor a pessoa acertar de primeira do que ler o erro. -->
      <ul class="flex flex-col gap-0.5 -mt-2">
        <li
          v-for="r in requisitos"
          :key="r.chave"
          class="flex items-center gap-2 text-xs"
          :class="r.ok ? 'text-n-teal-11' : 'text-n-slate-11'"
        >
          <span
            class="size-3.5 shrink-0"
            :class="r.ok ? 'i-lucide-check' : 'i-lucide-dot'"
          />
          {{ $t(`AGENT_MGMT.SENHA.REQUISITO.${r.chave}`) }}
        </li>
      </ul>

      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_MGMT.SENHA.CONFIRMAR') }}
        </span>
        <input
          v-model="confirmacao"
          :type="mostrar ? 'text' : 'password'"
          autocomplete="new-password"
          class="mb-0"
        />
        <span
          v-if="confirmacao.length > 0 && !combinam"
          class="text-xs text-n-ruby-11"
        >
          {{ $t('AGENT_MGMT.SENHA.NAO_COMBINAM') }}
        </span>
      </label>

      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input v-model="mostrar" type="checkbox" class="m-0" />
        {{ $t('AGENT_MGMT.SENHA.MOSTRAR') }}
      </label>

      <p class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11">
        {{ $t('AGENT_MGMT.SENHA.AVISO') }}
      </p>

      <div class="flex items-center justify-end gap-2">
        <Button
          faded
          slate
          type="button"
          :label="$t('AGENT_MGMT.SENHA.CANCELAR')"
          @click="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.SENHA.SALVAR')"
          :is-loading="salvando"
          :disabled="!combinam || !todosOsRequisitos || salvando"
        />
      </div>
    </form>
  </div>
</template>
